import { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useAuth, useLocale, useSettings } from '../context';
import { isWowSmartAllowed } from '../wowsmart/allowlist';
import { useSiteContent } from '../context/SiteContentContext';
import { casinoRouteForLobby } from '../data/casinoData';
import { readRoute } from '../navigation';
import { prefetchCasinoGameFeed, prefetchCasinoVendors } from '../hooks/useCasinoGameFeed';
import Logo from './Logo';
import { HeaderAuthSkeleton, LogoSkeleton } from './home/Skeleton';
import { IconGift, IconHelp, IconSearch } from './HeaderIcons';
import LanguageMenu from './LanguageMenu';
import HeaderClock from './HeaderClock';
import NotificationsDropdown from './NotificationsDropdown';
import AccountMenu from './AccountMenu';
import BalanceSum from './BalanceSum';
import { formatBalanceParts } from '../utils/balanceDisplay';
import { IconHide, IconPlusCircle, IconPlusSmall, IconShow } from './VurgunIcons';
import HoverTip from './HoverTip';
import HeaderNavIcon from './HeaderNavIcon';
import { helpPath } from '../data/helpData';
import { enUpper } from '../utils/latinCase';
import { headerDisplayBalance } from '../utils/walletBonusContext';

const NEW_CASINO_TAB_ID = '447';

/** Turkish nav needs dotted İ (BAHİS); en-US upper turns i → I. */
function navUpper(value, locale) {
  const loc = String(locale || '').toLowerCase();
  if (loc === 'tr' || loc.startsWith('tr-')) {
    return String(value ?? '').toLocaleUpperCase('tr-TR');
  }
  return enUpper(value);
}


function newNavDropdown(tr) {
  return [
    {
      id: 'new-games',
      label: tr('nav.newGames') || 'Yeni Oyunlar',
      pageId: 'casino',
      lobbyKey: 'slots',
      casinoTabId: NEW_CASINO_TAB_ID,
    },
    {
      id: 'new-promos',
      label: tr('nav.newPromos') || 'Yeni Promosyonlar',
      pageId: 'promos',
    },
  ];
}

function desktopMainNav(tr) {
  return [
    {
      id: 'new',
      label: tr('nav.new') || 'YENİ',
      pageId: 'casino',
      lobbyKey: 'slots',
      casinoTabId: NEW_CASINO_TAB_ID,
      dropdown: true,
    },
    { id: 'slots', label: tr('nav.slots'), pageId: 'casino', lobbyKey: 'slots' },
    { id: 'sport', label: tr('nav.sport'), pageId: 'sport' },
    {
      id: 'sport-live',
      label: tr('nav.sportLive') || 'Canlı bahis',
      pageId: 'sport',
      sportFilter: 'live',
    },
    { id: 'live', label: tr('nav.liveCasino') || 'Canlı Casino', pageId: 'casino', lobbyKey: 'live' },
    { id: 'aviator', label: tr('nav.aviator') || 'Aviator', pageId: 'casino', casinoGameId: '112982' },
    { id: 'fast', label: tr('nav.fast'), pageId: 'casino', lobbyKey: 'fast' },
    { id: 'poker', label: tr('nav.poker'), pageId: 'casino', lobbyKey: 'poker' },
    { id: 'tv', label: tr('nav.vurgunTv') || 'Vurgun TV', pageId: 'vurgunTv' },
  ];
}

function desktopSubNav(tr) {
  return [
    { id: 'vip', label: tr('nav.vipClub'), pageId: 'vip' },
    { id: 'promos', label: tr('nav.promos'), pageId: 'promos' },
    { id: 'wowsmart', label: 'Hazine', wowsmartDp: 'dp:gf' },
    { id: 'help', label: tr('header.support') || tr('nav.help') || 'Yardım', pageId: 'help' },
  ];
}

function navItemLabel(item) {
  if (item.label) return item.label;
  const pageId = item.pageId || item.id;
  return pageId || '';
}

function prefetchCasinoNavItem(item) {
  const pageId = item.pageId || item.id;
  if (pageId !== 'casino' || item.casinoGameId) return;
  const lobbyKey = item.lobbyKey || 'slots';
  prefetchCasinoVendors(lobbyKey);
  prefetchCasinoGameFeed({
    lobby: lobbyKey,
    category: 'top',
    sort: 'popular',
    pageSize: 16,
  });
  prefetchCasinoGameFeed({
    lobby: lobbyKey,
    category: 'new',
    sort: 'popular',
    pageSize: 16,
  });
  prefetchCasinoGameFeed({
    lobby: lobbyKey,
    category: 'all',
    sort: 'popular',
    pageSize: 21,
  });
  prefetchCasinoGameFeed({
    lobby: lobbyKey,
    category: 'all',
    sort: 'default',
    pageSize: 21,
  });
}

function navigateByNavItem(item, onNavigate) {
  if (item.wowsmartDp) {
    window._wowsmart?.dp(item.wowsmartDp);
    return;
  }
  const pageId = item.pageId || item.id;
  if (pageId === 'home') {
    onNavigate('home');
    return;
  }
  if (pageId === 'sport') {
    onNavigate('sport', {
      sportFilter: item.sportFilter === 'live' ? 'live' : 'all',
    });
    return;
  }
  if (pageId === 'casino') {
    if (item.casinoGameId) {
      onNavigate('casino', {
        casinoGameId: String(item.casinoGameId),
        casinoGameDemo: false,
      });
      return;
    }
    const route = item.lobbyKey ? casinoRouteForLobby(item.lobbyKey) : {};
    onNavigate('casino', {
      casinoTabId: item.casinoTabId || route.casinoTabId || '-1',
      casinoLobbyId: item.casinoLobbyId ?? route.casinoLobbyId ?? null,
      casinoGameId: null,
      casinoGameDemo: false,
      casinoProvider: null,
      casinoQuery: null,
    });
    return;
  }
  if (pageId === 'promos') {
    onNavigate('promos');
    return;
  }
  if (pageId === 'vip') {
    onNavigate('vip');
    return;
  }
  if (pageId === 'vurgunTv') {
    onNavigate('vurgunTv');
    return;
  }
  if (pageId === 'help') {
    onNavigate('help');
    return;
  }
  onNavigate('home');
}

function isNavItemActive(item, page, route, sportFilter) {
  const pageId = item.pageId || item.id;
  if (pageId === 'home') return page === 'home';
  if (pageId === 'promos') return page === 'promos';
  if (pageId === 'vip') return page === 'vip';
  if (pageId === 'vurgunTv') return page === 'vurgunTv';
  if (pageId === 'help') return page === 'help';
  if (pageId === 'sport') {
    if (page !== 'sport') return false;
    if (item.sportFilter === 'live') return sportFilter === 'live';
    return sportFilter !== 'live';
  }
  if (pageId === 'casino') {
    if (page !== 'casino') return false;
    if (item.casinoGameId) {
      return String(route.casinoGameId || '') === String(item.casinoGameId);
    }
    if (route.casinoGameId) return false;
    if (item.casinoTabId) {
      return String(route.casinoTabId || '') === String(item.casinoTabId);
    }
    if (String(route.casinoTabId || '') === NEW_CASINO_TAB_ID) return false;
    const routeOpts = item.lobbyKey ? casinoRouteForLobby(item.lobbyKey) : {};
    const targetLobby = item.casinoLobbyId ?? routeOpts.casinoLobbyId ?? null;
    const currentLobby = route.casinoLobbyId ?? null;
    if (targetLobby == null) return currentLobby == null;
    return Number(currentLobby) === Number(targetLobby);
  }
  return page === pageId;
}

function navItemHref(item) {
  if (item.wowsmartDp) return '/#/_wowsmart_dp=gf';
  const pageId = item.pageId || item.id;
  if (pageId === 'sport') {
    return item.sportFilter === 'live' ? '/sport?type=live' : '/sport';
  }
  if (pageId === 'promos') return '/promos/actions';
  if (pageId === 'vip') return '/vip';
  if (pageId === 'vurgunTv') return '/vurgun-tv';
  if (pageId === 'help') return '/help';
  if (pageId === 'casino') {
    if (item.casinoGameId) return `/casino/game/${item.casinoGameId}`;
    const route = item.lobbyKey ? casinoRouteForLobby(item.lobbyKey) : {};
    const tab = item.casinoTabId || route.casinoTabId || '-1';
    const lobby = item.casinoLobbyId ?? route.casinoLobbyId ?? null;
    if (lobby != null) return `/casino/${tab}/${lobby}`;
    return `/casino/${tab}`;
  }
  return '/';
}


function navItemKey(item) {
  const filter = item.sportFilter ? `-${item.sportFilter}` : '';
  return `${item.pageId || item.id}-${item.id || item.label || ''}${filter}`;
}

function isNewNavItem(item) {
  return item.id === 'new' || item.casinoTabId === NEW_CASINO_TAB_ID;
}

function computeVisibleNavCount(itemWidths, moreWidth, available, gap) {
  const n = itemWidths.length;
  if (n === 0) return 0;
  const sum = (count, withMore) => {
    let total = 0;
    for (let i = 0; i < count; i += 1) total += itemWidths[i];
    const slots = count + (withMore ? 1 : 0);
    if (slots > 1) total += (slots - 1) * gap;
    if (withMore) total += moreWidth;
    return total;
  };
  if (sum(n, false) <= available) return n;
  let count = 0;
  for (let i = 1; i <= n; i += 1) {
    if (sum(i, true) <= available) count = i;
    else break;
  }
  return count;
}

export default function Header({
  page,
  onNavigate,
  onLogin,
  onRegister,
  onCasinoSearch,
  isMobile = false,
  sportFilter = 'all',
  unreadNotifications = 0,
  onUnreadChange,
  accountMenuRef: accountMenuRefProp,
  onAccountSheetChange,
}) {
  const { user, logout, loading: authLoading } = useAuth();
  const sessionPending = authLoading && !user;
  const _locale = useLocale();
  const tr = _locale.t;
  const locale = _locale.locale;
  const { settings, updateSettings } = useSettings();
  const { nav, logo, loading: cmsLoading } = useSiteContent();
  const authMode = page === 'register' || page === 'login';
  const [pathname, setPathname] = useState(() => window.location.pathname);
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [visibleNavCount, setVisibleNavCount] = useState(null);
  const newMenuCloseTimer = useRef(null);
  const accountMenuRefLocal = useRef(null);
  const accountMenuRef = accountMenuRefProp || accountMenuRefLocal;
  const navScrollRef = useRef(null);
  const measureNavRef = useRef(null);
  const moreMeasureRef = useRef(null);
  const moreWrapRef = useRef(null);

  const openNewMenu = () => {
    if (newMenuCloseTimer.current) {
      window.clearTimeout(newMenuCloseTimer.current);
      newMenuCloseTimer.current = null;
    }
    setMoreMenuOpen(false);
    setNewMenuOpen(true);
  };

  const scheduleCloseNewMenu = () => {
    if (newMenuCloseTimer.current) window.clearTimeout(newMenuCloseTimer.current);
    newMenuCloseTimer.current = window.setTimeout(() => setNewMenuOpen(false), 140);
  };

  const closeMoreMenu = () => setMoreMenuOpen(false);

  const toggleMoreMenu = () => {
    setNewMenuOpen(false);
    setMoreMenuOpen((open) => !open);
  };

  useEffect(() => () => {
    if (newMenuCloseTimer.current) window.clearTimeout(newMenuCloseTimer.current);
  }, []);

  useEffect(() => {
    setPathname(window.location.pathname);
  }, [page]);

  useEffect(() => {
    const sync = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', sync);
    window.addEventListener('vb-route-change', sync);
    return () => {
      window.removeEventListener('popstate', sync);
      window.removeEventListener('vb-route-change', sync);
    };
  }, []);

  const route = useMemo(() => readRoute(), [pathname, page]);
  const balanceView = useMemo(
    () => headerDisplayBalance(user, { page, route }),
    [user, page, route],
  );
  const inGame = Boolean(route?.casinoGameId);
  const displayBalanceAmount = balanceView.amount;
  const showBonusBadge = Boolean(user && balanceView.showBadge && !inGame);
  const showDualBalance = Boolean(user && balanceView.showDual && !inGame);
  const cashBalanceAmount = Number(balanceView.cashAmount ?? user?.balance ?? 0);
  const bonusBalanceAmount = Number(balanceView.bonusAmount ?? user?.bonus_balance ?? 0);

  const mainNav = useMemo(() => {
    const fallback = desktopMainNav(tr);
    const newItem = fallback.find((item) => item.id === 'new');
    let base;
    if (isMobile && !cmsLoading && nav?.header_main?.length) base = nav.header_main;
    else if (!isMobile) base = fallback;
    else if (!cmsLoading && nav?.header_main?.length) base = nav.header_main;
    else base = fallback;
    if (!newItem) return base;
    if ((base || []).some((item) => item.id === 'new' || item.casinoTabId === NEW_CASINO_TAB_ID)) return base;
    return [newItem, ...(base || [])];
  }, [isMobile, cmsLoading, nav?.header_main, tr]);

  const newDropdownItems = useMemo(() => newNavDropdown(tr), [tr]);

  const navItems = mainNav || [];
  const overflowEnabled = !isMobile;
  const fittedCount = overflowEnabled
    ? (visibleNavCount == null ? navItems.length : visibleNavCount)
    : navItems.length;
  const visibleNavItems = overflowEnabled ? navItems.slice(0, fittedCount) : navItems;
  const overflowNavItems = overflowEnabled ? navItems.slice(fittedCount) : [];
  const hasOverflow = overflowNavItems.length > 0;
  const menuOpen = newMenuOpen || moreMenuOpen;

  useLayoutEffect(() => {
    if (!overflowEnabled) {
      setVisibleNavCount(null);
      return undefined;
    }
    const container = navScrollRef.current;
    const measureNav = measureNavRef.current;
    if (!container || !measureNav) return undefined;

    const recalc = () => {
      const available = container.clientWidth;
      const measureItems = measureNav.querySelectorAll('[data-nav-measure="item"]');
      const widths = Array.from(measureItems).map((el) => el.getBoundingClientRect().width);
      const moreWidth = moreMeasureRef.current
        ? moreMeasureRef.current.getBoundingClientRect().width
        : 44;
      const styles = window.getComputedStyle(measureNav);
      const gapRaw = styles.columnGap || styles.gap || '2';
      const gap = Number.parseFloat(gapRaw) || 2;
      const next = computeVisibleNavCount(widths, moreWidth, available, gap);
      setVisibleNavCount((prev) => (prev === next ? prev : next));
    };

    recalc();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(recalc) : null;
    ro?.observe(container);
    window.addEventListener('resize', recalc);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', recalc);
    };
  }, [overflowEnabled, navItems, locale, tr]);

  useEffect(() => {
    if (!hasOverflow && moreMenuOpen) setMoreMenuOpen(false);
  }, [hasOverflow, moreMenuOpen]);

  useEffect(() => {
    if (!moreMenuOpen) return undefined;
    const onPointerDown = (event) => {
      if (!moreWrapRef.current) return;
      if (!moreWrapRef.current.contains(event.target)) closeMoreMenu();
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeMoreMenu();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [moreMenuOpen]);

  const ctrlNav = !cmsLoading && nav?.header_ctrl?.length ? nav.header_ctrl : null;

  const goCashier = (e) => {
    e?.preventDefault?.();
    if (isMobile && accountMenuRef.current?.openSection) {
      accountMenuRef.current.openSection('cashier');
      return;
    }
    onNavigate('account', { accountSection: 'cashier' });
  };

  const openSearch = () => onCasinoSearch?.();

  const onSearchKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openSearch();
    }
  };

  const openLogin = (e) => {
    e?.preventDefault?.();
    onLogin();
  };

  const renderAuthButtons = () => (
    <div className="header-auth">
      {page !== 'login' && (
        <a
          href="/authProcess/login"
          className={`btn-login${page === 'login' ? ' is-active' : ''}`}
          onClick={(e) => { e.preventDefault(); onLogin(); }}
        >
          {tr('header.login')}
        </a>
      )}
      {page !== 'register' && (
        <a
          href="/authProcess/registration"
          className={`btn-join${page === 'register' ? ' is-active' : ''}`}
          onClick={(e) => { e.preventDefault(); onRegister(); }}
        >
          {tr('header.join')}
        </a>
      )}
    </div>
  );

  const subNav = useMemo(() => {
    const items = desktopSubNav(tr);
    if (isWowSmartAllowed(user)) return items;
    return items.filter((item) => !item.wowsmartDp);
  }, [tr, user]);

  const renderSubBar = () => (
    <div className="header__sub">
      <button
        type="button"
        className={`header__home-btn${page === 'home' ? ' is-active' : ''}`}
        aria-label={tr('nav.home')}
        onClick={() => onNavigate('home')}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <path fill="currentColor" d="M12 3.2 3.5 10.2V21h6.2v-6.3h4.6V21H20.5V10.2L12 3.2z" />
        </svg>
      </button>
      <div className="header__sub-links">
        {subNav.map((item) => {
          const pageId = item.pageId || item.id;
          const label = navItemLabel(item);
          const active = isNavItemActive(item, page, route, sportFilter);
          return (
            <a
              key={`sub-${pageId}-${label}`}
              href="#"
              className={`header__sub-link${active ? ' is-active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                navigateByNavItem(item, onNavigate);
              }}
            >
              <span className="header__sub-link__label">{label}</span>
            </a>
          );
        })}
      </div>
      <div className="header__announce" role="status">
        <span className="header__announce-icon" aria-hidden="true">
          <img src="/header-nav/megaphone.png" alt="" width={20} height={20} draggable={false} />
        </span>
        <p className="header__announce-text">
          <span>Sürekli güncel adresimiz:{' '}</span>
          <a
            className="header__announce-link"
            href="https://t.ly/vurgunbet"
            target="_blank"
            rel="noopener noreferrer"
          >
            t.ly/vurgunbet
          </a>
        </p>
      </div>
      <div className="header__sub-actions">
        <span
          className="header-ctrl header-ctrl--icon"
          role="button"
          tabIndex={0}
          aria-label={tr('header.search')}
          onClick={openSearch}
          onKeyDown={onSearchKey}
        >
          <IconSearch />
        </span>
        <LanguageMenu />
        <HeaderClock />
      </div>
    </div>
  );

  const renderControls = () => (
    <>
      {!cmsLoading && !isMobile && ctrlNav?.map((item) => {
        const pageId = item.pageId || item.id;
        const label = navItemLabel(item);
        const isPromos = pageId === 'promos';
        return (
          <a
            key={`${pageId}-${label}`}
            href={isPromos ? '/promos/actions' : helpPath('faq', null, null, locale)}
            className={`header-ctrl header-ctrl--${isPromos ? 'promos' : 'help'}${page === pageId ? ' is-active' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              navigateByNavItem(item, onNavigate);
            }}
          >
            {isPromos ? <IconGift /> : <IconHelp />}
            <span>{label}</span>
          </a>
        );
      })}
      {!isMobile && <span className="header-separator" aria-hidden="true" />}
      {!isMobile && !authMode && (
        <span
          className="header-ctrl header-ctrl--icon"
          role="button"
          tabIndex={0}
          aria-label={tr('header.search')}
          onClick={openSearch}
          onKeyDown={onSearchKey}
        >
          <IconSearch />
        </span>
      )}
      {user && (
        <NotificationsDropdown
          unread={unreadNotifications}
          onUnreadChange={onUnreadChange}
          onNavigate={onNavigate}
        />
      )}
      {user ? (
        isMobile ? (
          <div className="header-account-data">
            <a
              href="/account/payments"
              className={`header-mobile-balance${inGame ? ' header-mobile-balance--ingame' : ''}${showBonusBadge ? ' header-mobile-balance--bonus' : ''}${showDualBalance ? ' header-mobile-balance--dual' : ''}`}
              onClick={goCashier}
            >
              <span className="header-mobile-balance__plus" aria-hidden="true">
                <IconPlusSmall />
              </span>
              {inGame ? (
                <span className="header-mobile-balance__ingame">{tr('header.inGame') || '(Oyunda)'}</span>
              ) : showDualBalance ? (
                <span className={`header-mobile-balance__dual${settings.hideBalance ? ' is-hidden' : ''}`}>
                  <span className="header-mobile-balance__cash-row">
                    <span className="header-mobile-balance__cash-amount">
                      {formatBalanceParts(cashBalanceAmount, settings.hideBalance).amount}
                    </span>
                    <span className="header-mobile-balance__cash-sign">
                      {formatBalanceParts(cashBalanceAmount, settings.hideBalance).sign}
                    </span>
                  </span>
                  <span className="header-mobile-balance__bonus-row">
                    <span className="header-mobile-balance__bonus-icon" aria-hidden="true">
                      <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 22 22" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M5.95721 1.21952C7.33046 0.521666 8.54341 0.176842 10.1742 0.0207022C10.525 -0.0128731 11.5216 -0.0041461 11.96 0.0363076C13.3706 0.166525 14.5582 0.502364 15.8328 1.13152C17.0522 1.73347 17.9638 2.39638 18.8931 3.35721C19.7312 4.22359 20.328 5.0677 20.8642 6.14473C21.6807 7.78498 22.0581 9.52995 21.9928 11.3634C21.9321 13.067 21.543 14.5526 20.7655 16.0499C19.9061 17.7048 18.6082 19.1464 17.0376 20.1905C16.4656 20.5708 15.353 21.1279 14.7157 21.3531C12.613 22.0963 10.4545 22.1997 8.30239 21.6604C7.48223 21.4548 6.95988 21.2619 6.15085 20.8654C4.99139 20.2971 4.1695 19.7063 3.22239 18.7601C2.26483 17.8034 1.68482 16.9949 1.09267 15.7914C0.569287 14.7277 0.234292 13.6185 0.0539929 12.3522C-0.0179976 11.8466 -0.0179976 10.1494 0.0539929 9.64382C0.157611 8.91622 0.327152 8.18225 0.540585 7.53731C0.805009 6.7383 1.35572 5.60671 1.80638 4.93641C2.87075 3.3533 4.33048 2.04614 5.95721 1.21952ZM19.7287 6.76125C18.2408 3.70401 15.2508 1.6349 11.8739 1.32567C9.30742 1.09064 6.70604 1.9136 4.70932 3.59224C2.56041 5.39881 1.26694 8.18156 1.26694 10.998C1.26694 13.8133 2.56665 16.6089 4.70932 18.4023C6.11454 19.5784 7.7315 20.3102 9.59332 20.6126C10.254 20.7199 11.7288 20.72 12.3903 20.6128C13.475 20.4369 14.3013 20.1792 15.2303 19.7272C17.2105 18.7637 18.7685 17.2077 19.7287 15.2348C20.1822 14.3028 20.4397 13.4785 20.6157 12.3952C20.7229 11.7349 20.7229 10.2612 20.6157 9.60083C20.4397 8.51757 20.1822 7.69319 19.7287 6.76125ZM11.5643 6.59988C11.3047 6.1809 10.6952 6.1809 10.4356 6.59988L9.05039 8.83569C8.99637 8.92288 8.92288 8.99637 8.83569 9.05039L6.59988 10.4356C6.1809 10.6952 6.1809 11.3047 6.59988 11.5643L8.83569 12.9495C8.92288 13.0035 8.99637 13.077 9.05039 13.1642L10.4356 15.4C10.6952 15.819 11.3047 15.819 11.5643 15.4L12.9495 13.1642C13.0035 13.077 13.077 13.0035 13.1642 12.9495L15.4 11.5643C15.819 11.3047 15.819 10.6952 15.4 10.4356L13.1642 9.05039C13.077 8.99637 13.0035 8.92288 12.9495 8.83569L11.5643 6.59988Z" fill="currentColor" />
                      </svg>
                    </span>
                    <span className="header-mobile-balance__bonus-amount">
                      {formatBalanceParts(bonusBalanceAmount, settings.hideBalance).full}
                    </span>
                  </span>
                </span>
              ) : (
                <>
                  {showBonusBadge ? (
                    <span className="header-balance-badge header-balance-badge--gift" aria-hidden="true">
                      <IconGift />
                    </span>
                  ) : null}
                  <BalanceSum
                    amount={displayBalanceAmount}
                    hidden={settings.hideBalance}
                    className={`header-mobile-balance__sum${settings.hideBalance ? ' is-hidden' : ''}${showBonusBadge ? ' is-bonus' : ''}`}
                  />
                </>
              )}
            </a>
            <AccountMenu
              ref={accountMenuRef}
              iconOnly
              balance={displayBalanceAmount}
              inGame={inGame}
              onNavigate={onNavigate}
              onLogout={logout}
              unreadNotifications={unreadNotifications}
              onUnreadChange={onUnreadChange}
              onSheetChange={onAccountSheetChange}
            />
          </div>
        ) : (
          <div className={`header-wallet${showBonusBadge ? ' header-wallet--bonus' : ''}`}>
            <AccountMenu
              balance={displayBalanceAmount}
              inGame={inGame}
              onNavigate={onNavigate}
              onLogout={logout}
            />
            {showBonusBadge ? <span className="header-balance-badge header-balance-badge--desk">Bonus</span> : null}
            <div className="balanceContainer">
              <HoverTip
                text={settings.hideBalance ? tr('header.showBalance') : tr('header.hideBalance')}
                className="balanceContainer__toggle-tip"
                placement="bottom"
              >
                <button
                  type="button"
                  className={`balanceContainer__toggle${settings.hideBalance ? ' is-active' : ''}`}
                  aria-label={settings.hideBalance ? tr('header.showBalance') : tr('header.hideBalance')}
                  aria-pressed={settings.hideBalance}
                  onClick={() => updateSettings({ hideBalance: !settings.hideBalance })}
                >
                  {settings.hideBalance ? <IconShow /> : <IconHide />}
                </button>
              </HoverTip>
              <HoverTip text={tr('header.deposit')} className="replenishButton-tip" placement="bottom">
                <a href="/account/payments" className="replenishButton" onClick={goCashier}>
                  <span className="replenishButton__icon" aria-hidden="true">
                    <IconPlusCircle />
                  </span>
                  <span className="replenishButton__text">{tr('header.deposit')}</span>
                </a>
              </HoverTip>
            </div>
          </div>
        )
      ) : sessionPending ? (
        !authMode ? <HeaderAuthSkeleton /> : null
      ) : (
        isMobile && !authMode ? (
          <div className="header-auth">
            <a
              href="/authProcess/registration"
              className="btn-join"
              onClick={(e) => { e.preventDefault(); onRegister(); }}
            >
              {tr('header.join')}
            </a>
            <a
              href="/authProcess/login"
              className="btn-login btn-login--accent"
              onClick={openLogin}
            >
              {tr('header.login')}
            </a>
          </div>
        ) : (
          !isMobile && renderAuthButtons()
        )
      )}
    </>
  );

  const renderNavLink = (item, textOnly, extraClass = '') => {
    const pageId = item.pageId || item.id;
    const label = navItemLabel(item);
    const active = isNavItemActive(item, page, route, sportFilter);
    const isNew = isNewNavItem(item);
    const href = navItemHref(item);
    return (
      <a
        key={navItemKey(item)}
        href={href}
        className={`header-nav-item${isNew ? ' header-nav-item--new' : ''}${active ? ' active' : ''}${extraClass}`}
        onClick={(e) => {
          e.preventDefault();
          navigateByNavItem(item, onNavigate);
          closeMoreMenu();
          setNewMenuOpen(false);
        }}
        onPointerEnter={() => prefetchCasinoNavItem(item)}
        onFocus={() => prefetchCasinoNavItem(item)}
      >
        {!textOnly ? <HeaderNavIcon item={item} /> : null}
        <span className="header-nav-item__label">{navUpper(label, locale)}</span>
      </a>
    );
  };

  const renderNewDropdownItem = (item, textOnly) => {
    const pageId = item.pageId || item.id;
    const label = navItemLabel(item);
    const active = isNavItemActive(item, page, route, sportFilter);
    const href = navItemHref(item);
    return (
      <div
        key={navItemKey(item)}
        className={`header-nav-item header-nav-item--new header-nav-item--has-menu${active || newMenuOpen ? ' active' : ''}${newMenuOpen ? ' is-open' : ''}`}
        onMouseEnter={openNewMenu}
        onMouseLeave={scheduleCloseNewMenu}
        onFocus={openNewMenu}
      >
        <a
          href={href}
          className="header-nav-item__trigger"
          aria-haspopup="menu"
          aria-expanded={newMenuOpen}
          onClick={(e) => {
            e.preventDefault();
            navigateByNavItem(item, onNavigate);
            setNewMenuOpen(false);
          }}
          onPointerEnter={() => prefetchCasinoNavItem(item)}
        >
          {!textOnly ? <HeaderNavIcon item={item} /> : null}
          <span className="header-nav-item__label">{navUpper(label, locale)}</span>
          <span className="header-nav-item__chev" aria-hidden="true" />
        </a>
        {newMenuOpen ? (
          <div
            className="header-nav-menu"
            role="menu"
            onMouseEnter={openNewMenu}
            onMouseLeave={scheduleCloseNewMenu}
          >
            {newDropdownItems.map((sub) => (
              <a
                key={sub.id}
                href={navItemHref(sub)}
                role="menuitem"
                className="header-nav-menu__item"
                onClick={(e) => {
                  e.preventDefault();
                  navigateByNavItem(sub, onNavigate);
                  setNewMenuOpen(false);
                }}
              >
                {navItemLabel(sub)}
              </a>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  const renderOverflowEntries = () => {
    const entries = [];
    overflowNavItems.forEach((item) => {
      const isNew = isNewNavItem(item);
      const showNested = isNew && item.dropdown;
      if (showNested) {
        entries.push(
          <div key={`ov-label-${navItemKey(item)}`} className="header-nav-menu__label" role="presentation">
            {navUpper(navItemLabel(item), locale)}
          </div>
        );
        newDropdownItems.forEach((sub) => {
          entries.push(
            <a
              key={`ov-${sub.id}`}
              href={navItemHref(sub)}
              role="menuitem"
              className="header-nav-menu__item"
              onClick={(e) => {
                e.preventDefault();
                navigateByNavItem(sub, onNavigate);
                closeMoreMenu();
              }}
            >
              {navItemLabel(sub)}
            </a>
          );
        });
        return;
      }
      const active = isNavItemActive(item, page, route, sportFilter);
      entries.push(
        <a
          key={`ov-${navItemKey(item)}`}
          href={navItemHref(item)}
          role="menuitem"
          className={`header-nav-menu__item${isNew ? ' header-nav-menu__item--new' : ''}${active ? ' is-active' : ''}`}
          onClick={(e) => {
            e.preventDefault();
            navigateByNavItem(item, onNavigate);
            closeMoreMenu();
          }}
          onPointerEnter={() => prefetchCasinoNavItem(item)}
        >
          {navUpper(navItemLabel(item), locale)}
        </a>
      );
    });
    return entries;
  };

  const renderMainNav = (textOnly = false) => (
    <>
      {overflowEnabled ? (
        <div className="header-nav-measure" aria-hidden="true">
          <div className={`header-nav${textOnly ? ' header-nav--text' : ''}`} ref={measureNavRef}>
            {navItems.map((item) => {
              const isNew = isNewNavItem(item);
              const showDropdown = isNew && item.dropdown;
              if (showDropdown) {
                return (
                  <div
                    key={`m-${navItemKey(item)}`}
                    data-nav-measure="item"
                    className="header-nav-item header-nav-item--new header-nav-item--has-menu"
                  >
                    <span className="header-nav-item__trigger">
                      {!textOnly ? <HeaderNavIcon item={item} /> : null}
                      <span className="header-nav-item__label">{navUpper(navItemLabel(item), locale)}</span>
                      <span className="header-nav-item__chev" aria-hidden="true" />
                    </span>
                  </div>
                );
              }
              return (
                <span
                  key={`m-${navItemKey(item)}`}
                  data-nav-measure="item"
                  className={`header-nav-item${isNew ? ' header-nav-item--new' : ''}`}
                >
                  {!textOnly ? <HeaderNavIcon item={item} /> : null}
                  <span className="header-nav-item__label">{navUpper(navItemLabel(item), locale)}</span>
                </span>
              );
            })}
            <span
              ref={moreMeasureRef}
              data-nav-measure="more"
              className="header-nav-item header-nav-item--more header-nav-item--has-menu"
            >
              <span className="header-nav-item__trigger header-nav-item__more-btn">
                <span className="header-nav-item__label" aria-hidden="true">…</span>
              </span>
            </span>
          </div>
        </div>
      ) : null}
      <nav className={`header-nav${textOnly ? ' header-nav--text' : ''}${overflowEnabled ? ' header-nav--overflow' : ''}`} aria-label="Main" lang="en">
        {visibleNavItems.map((item) => {
          const isNew = isNewNavItem(item);
          const showDropdown = isNew && item.dropdown && !isMobile;
          if (showDropdown) return renderNewDropdownItem(item, textOnly);
          return renderNavLink(item, textOnly);
        })}
        {hasOverflow ? (
          <div
            ref={moreWrapRef}
            className={`header-nav-item header-nav-item--more header-nav-item--has-menu${moreMenuOpen ? ' is-open active' : ''}`}
          >
            <button
              type="button"
              className="header-nav-item__trigger header-nav-item__more-btn"
              aria-haspopup="menu"
              aria-expanded={moreMenuOpen}
              aria-label={tr('nav.more') || 'More'}
              onClick={toggleMoreMenu}
            >
              <span className="header-nav-item__label" aria-hidden="true">…</span>
            </button>
            {moreMenuOpen ? (
              <div className="header-nav-menu header-nav-menu--more" role="menu">
                {renderOverflowEntries()}
              </div>
            ) : null}
          </div>
        ) : null}
      </nav>
    </>
  );

  return (
    <header className={`header header--maxline${authMode ? ' header--auth' : ''}${user ? ' header--logged-in' : ''}${isMobile ? ' header--mobile' : ' header--desktop'}`}>
      <div className="header__inner">
        {isMobile ? (
          <>
            <a href="#" className="logo" onClick={(e) => { e.preventDefault(); onNavigate('home'); }} aria-label={logo?.siteName || 'Home'}>
              {logo ? (
                <Logo mode={logo.mode} text={logo.text} textSize={logo.textSize} textStyle={logo.textStyle} imageUrl={logo.imageUrl} alt={logo.alt} />
              ) : (
                <LogoSkeleton />
              )}
            </a>
            <div className="header-nav-scroll">
              {renderMainNav(true)}
            </div>
            <div className="header-controls">
              {renderControls()}
            </div>
          </>
        ) : (
          <>
            <div className="header__main">
              <a href="#" className="logo" onClick={(e) => { e.preventDefault(); onNavigate('home'); }} aria-label={logo?.siteName || 'Home'}>
                {logo ? (
                  <Logo mode={logo.mode} text={logo.text} textSize={logo.textSize} textStyle={logo.textStyle} imageUrl={logo.imageUrl} alt={logo.alt} />
                ) : (
                  <LogoSkeleton />
                )}
              </a>
              <div
                ref={navScrollRef}
                className={`header-nav-scroll${menuOpen ? ' is-menu-open' : ''}${hasOverflow ? ' has-overflow' : ''}`}
              >
                {renderMainNav(true)}
              </div>
              <div className="header-controls header-controls--main">
                {user ? (
                  <div className={`header-wallet${showBonusBadge ? ' header-wallet--bonus' : ''}`}>
                    <AccountMenu
                      balance={displayBalanceAmount}
                      inGame={inGame}
                      onNavigate={onNavigate}
                      onLogout={logout}
                    />
                    {showBonusBadge ? <span className="header-balance-badge header-balance-badge--desk">Bonus</span> : null}
                    <div className="balanceContainer">
                      <HoverTip
                        text={settings.hideBalance ? tr('header.showBalance') : tr('header.hideBalance')}
                        className="balanceContainer__toggle-tip"
                        placement="bottom"
                      >
                        <button
                          type="button"
                          className={`balanceContainer__toggle${settings.hideBalance ? ' is-active' : ''}`}
                          aria-label={settings.hideBalance ? tr('header.showBalance') : tr('header.hideBalance')}
                          aria-pressed={settings.hideBalance}
                          onClick={() => updateSettings({ hideBalance: !settings.hideBalance })}
                        >
                          {settings.hideBalance ? <IconShow /> : <IconHide />}
                        </button>
                      </HoverTip>
                      <HoverTip text={tr('header.deposit')} className="replenishButton-tip" placement="bottom">
                        <a href="/account/payments" className="replenishButton" onClick={goCashier}>
                          <span className="replenishButton__icon" aria-hidden="true">
                            <IconPlusCircle />
                          </span>
                          <span className="replenishButton__text">{tr('header.deposit')}</span>
                        </a>
                      </HoverTip>
                    </div>
                  </div>
                ) : sessionPending ? (
                  <HeaderAuthSkeleton />
                ) : (
                  renderAuthButtons()
                )}
                {user ? (
                  <NotificationsDropdown
                    unread={unreadNotifications}
                    onUnreadChange={onUnreadChange}
                    onNavigate={onNavigate}
                  />
                ) : null}
              </div>
            </div>
            {renderSubBar()}
          </>
        )}
      </div>
    </header>
  );
}
